import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Image as ImageIcon, Sparkles, Camera, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Assessments, Patients } from "@/lib/api";
import CameraCapture from "@/components/CameraCapture";
import ConsentNotice from "@/components/ConsentNotice";
import { PhotoChecklist } from "@/components/PhotoGuide";

async function resizeToDataURL(file, maxDim = 1200) {
  if (!file) throw new Error("no-file");
  if (!file.type?.startsWith("image/")) throw new Error("not-image");
  if (file.size > 25 * 1024 * 1024) throw new Error("too-large");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          let { width, height } = img;
          const ratio = Math.min(maxDim / width, maxDim / height, 1);
          width = Math.round(width * ratio); height = Math.round(height * ratio);
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          // Compressão adaptativa — se ainda > 1.5MB, baixa qualidade gradualmente
          let q = 0.88;
          let url = canvas.toDataURL("image/jpeg", q);
          while (url.length > 1.5 * 1024 * 1024 * 1.37 && q > 0.5) {
            q -= 0.1;
            url = canvas.toDataURL("image/jpeg", q);
          }
          resolve(url);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("decode-failed"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("read-failed"));
    reader.readAsDataURL(file);
  });
}

// Verificações leves da fotografia carregada (sem alterar a imagem).
function usePhotoWarnings(dataUrl) {
  const [warnings, setWarnings] = useState([]);
  useEffect(() => {
    if (!dataUrl) { setWarnings([]); return; }
    const img = new Image();
    img.onload = () => {
      const w = [];
      if (img.width > img.height) w.push("Fotografia na horizontal: prefira vertical para o corpo inteiro ocupar o enquadramento.");
      if (Math.min(img.width, img.height) < 600) w.push("Resolução baixa: a deteção de pontos pode ser menos fiável.");
      setWarnings(w);
    };
    img.src = dataUrl;
  }, [dataUrl]);
  return warnings;
}

function CaptureCard({ label, accentClass, dataUrl, onPick, onOpenCamera, testId }) {
  const warnings = usePhotoWarnings(dataUrl);
  return (
    <div className="group relative rounded-2xl border-2 border-dashed border-slate-800 transition min-h-[420px] bg-slate-900/40 overflow-hidden flex flex-col" data-testid={testId}>
      {dataUrl ? (
        <>
          <img src={dataUrl} alt={label} className="absolute inset-0 w-full h-full object-contain" />
          <div className="absolute top-3 left-3 text-xs text-slate-200 bg-slate-950/70 backdrop-blur px-3 py-1 rounded-full border border-slate-800">
            {label}
          </div>
          {warnings.length > 0 && (
            <div className="absolute top-12 left-3 right-3 space-y-1" data-testid={`${testId}-warnings`}>
              {warnings.map((t) => <div key={t} className="text-[11px] text-amber-200 bg-slate-950/80 border border-amber-500/30 rounded-lg px-2 py-1">{t}</div>)}
            </div>
          )}
          <div className="mt-auto relative flex justify-end p-3 gap-2">
            <Button size="sm" variant="outline" className="rounded-full border-slate-700 text-slate-200 bg-slate-950/70 backdrop-blur"
              onClick={() => onPick("")} data-testid={`${testId}-remove`}>
              <RefreshCw className="w-4 h-4 mr-1" /> Substituir
            </Button>
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
          <div className={`w-16 h-16 rounded-full border flex items-center justify-center ${accentClass}`}>
            <ImageIcon className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <div className="mt-4 font-display text-xl text-slate-50">{label}</div>
          <div className="text-sm text-slate-500 mt-1 max-w-xs">
            Usa a captura guiada para padronizar a posição, ou carrega uma fotografia existente.
          </div>

          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <Button
              onClick={onOpenCamera}
              data-testid={`${testId}-capture`}
              className="rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 px-5 glow-sky"
            >
              <Camera className="w-4 h-4 mr-2" /> Capturar foto
            </Button>
            <label className="inline-flex items-center gap-2 rounded-full border border-slate-700 text-slate-200 hover:bg-slate-800 px-5 py-2 cursor-pointer text-sm font-medium">
              <Upload className="w-4 h-4" /> Carregar foto
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={`${testId}-upload-input`}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const url = await resizeToDataURL(f);
                    onPick(url);
                  } catch (err) {
                    const msg = {
                      "not-image": "Ficheiro não é uma imagem válida.",
                      "too-large": "Imagem demasiado grande (máx. 25MB).",
                      "decode-failed": "Não foi possível ler a imagem.",
                      "read-failed": "Falha a abrir o ficheiro.",
                    }[err?.message] || "Não foi possível processar a imagem.";
                    toast.error(msg);
                  } finally {
                    e.target.value = "";
                  }
                }}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewAssessment() {
  const { id: patientId } = useParams();
  const nav = useNavigate();
  const [front, setFront] = useState("");
  const [side, setSide] = useState("");
  const [back, setBack] = useState("");
  const [creating, setCreating] = useState(false);
  const [cameraFor, setCameraFor] = useState(null); // null | "front" | "side" | "back"
  const [gender, setGender] = useState("neutro");
  const [gate, setGate] = useState({});
  const blocked = Object.values(gate).some((c) => c.mode === "block" && !c.ok);

  useEffect(() => {
    (async () => {
      try {
        const p = await Patients.get(patientId);
        setGender(p.gender || "neutro");
      } catch { /* ignore */ }
    })();
  }, [patientId]);

  const startAssessment = async () => {
    if (!front && !side && !back) { toast.error("Carrega ou captura pelo menos uma fotografia"); return; }
    setCreating(true);
    try {
      const a = await Assessments.create({
        patient_id: patientId,
        front_image: front || null,
        side_image: side || null,
        back_image: back || null,
      });
      toast.success("Avaliação criada");
      nav(`/assessments/${a.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível criar a avaliação");
      setCreating(false);
    }
  };

  const onCameraCapture = (url) => {
    if (cameraFor === "front") setFront(url);
    else if (cameraFor === "side") setSide(url);
    else if (cameraFor === "back") setBack(url);
    setCameraFor(null);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-10" data-testid="new-assessment-page">
      <Link to={`/patients/${patientId}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar ao paciente
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Passo 1 de 2</div>
        <h1 className="font-display text-4xl text-slate-50 mt-2">Captura de imagens</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Captura as três vistas (frontal, lateral e posterior) usando o módulo de captura guiada.
          Uma avaliação 360º permite análise mais precisa de escoliose, omoplatas e simetria global.
        </p>
        <a href="/guia-fotografico" target="_blank" rel="noreferrer" data-testid="photo-guide-link"
          className="inline-flex items-center gap-1.5 mt-3 text-sm text-sky-300 hover:text-sky-200 transition-colors">
          <Camera className="w-4 h-4" /> Guia das três fotografias (pode partilhar com o paciente)
        </a>
        <div className="mt-4"><PhotoChecklist /></div>
      </div>

      <div className="space-y-2">
        <ConsentNotice patientId={patientId} op="avaliacao" onState={(c) => setGate((g) => ({ ...g, avaliacao: c }))} />
        <ConsentNotice patientId={patientId} op="fotografias" onState={(c) => setGate((g) => ({ ...g, fotografias: c }))} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <CaptureCard
          label="Vista frontal"
          accentClass="bg-sky-500/10 border-sky-500/30 text-sky-400 glow-sky"
          dataUrl={front}
          onPick={setFront}
          onOpenCamera={() => setCameraFor("front")}
          testId="capture-front"
        />
        <CaptureCard
          label="Vista lateral"
          accentClass="bg-emerald-500/10 border-emerald-500/30 text-emerald-400 glow-emerald"
          dataUrl={side}
          onPick={setSide}
          onOpenCamera={() => setCameraFor("side")}
          testId="capture-side"
        />
        <CaptureCard
          label="Vista posterior"
          accentClass="bg-violet-500/10 border-violet-500/30 text-violet-300"
          dataUrl={back}
          onPick={setBack}
          onOpenCamera={() => setCameraFor("back")}
          testId="capture-back"
        />
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="start-assessment-btn"
          disabled={creating || blocked}
          onClick={startAssessment}
          className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-8 py-6 font-semibold glow-sky"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {creating ? "A criar…" : "Continuar para marcação"}
        </Button>
      </div>

      {cameraFor && (
        <CameraCapture
          view={cameraFor}
          gender={gender}
          onCapture={onCameraCapture}
          onCancel={() => setCameraFor(null)}
        />
      )}
    </div>
  );
}
